import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  DateInput,
  Toolbar,
  SaveButton,
  DeleteButton,
  usePermissions,
} from 'react-admin';
import { Typography, Card, CardContent, Grid } from '@mui/material';

const gameTypeChoices = [
  { id: 'grid', name: 'Grid (2D)' },
  { id: 'grid_3d', name: 'Grid (3D)' },
  { id: 'higher_lower', name: 'Higher/Lower' },
  { id: 'timeline', name: 'Timeline' },
  { id: 'fact_or_cap', name: 'Fact or Cap' },
  { id: 'odd_one_out', name: 'Odd One Out' },
  { id: 'connections', name: 'Connections' },
  { id: 'guess_the_movie', name: 'Guess the Movie' },
  { id: 'redacted_plot', name: 'Redacted Plot' },
];

const entityTypeChoices = [
  { id: 'cricket_player', name: 'Cricket Player' },
  { id: 'movie', name: 'Movie' },
  { id: 'movie_song', name: 'Movie Song' },
];

// JSON input component
const JsonInput = ({ source, label }: { source: string; label: string }) => {
  return (
    <TextInput
      source={source}
      label={label}
      multiline
      fullWidth
      minRows={15}
      maxRows={40}
      format={(value: unknown) =>
        typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value || '{}')
      }
      parse={(value: string) => {
        try {
          return JSON.parse(value);
        } catch (e) {
          return value;
        }
      }}
      sx={{
        '& .MuiInputBase-input': {
          fontFamily: 'monospace',
          fontSize: '0.875rem',
        },
      }}
    />
  );
};

const EditToolbar = () => {
  const { permissions } = usePermissions();
  const canDelete =
    permissions?.role === 'super_admin' ||
    permissions?.permissions?.includes('game_configs:delete');

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton />
      {canDelete && <DeleteButton />}
    </Toolbar>
  );
};

export const GameConfigEdit = () => {
  return (
    <Edit>
      <SimpleForm toolbar={<EditToolbar />}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Game Settings
                </Typography>

                <DateInput source="date" fullWidth required />

                <SelectInput
                  source="game_type"
                  choices={gameTypeChoices}
                  fullWidth
                  required
                />

                <SelectInput
                  source="entity_type"
                  choices={entityTypeChoices}
                  fullWidth
                  required
                />

                <TextInput
                  source="category"
                  fullWidth
                  helperText="Optional category (e.g., 'ipl', 'tamil')"
                />

                <TextInput
                  source="slug"
                  fullWidth
                  helperText="SEO-friendly URL slug (auto-generated if empty)"
                />

                <TextInput
                  source="seo_description"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  helperText="SEO description for search engines (auto-generated if empty)"
                />

                <TextInput
                  source="post_game_trivia"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  helperText="Unique trivia about this puzzle's answers, shown after game completion"
                />

                <JsonInput source="companion" label="Companion Article (JSON)" />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Configuration (JSON)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  The game configuration containing criteria, answers, and other settings.
                </Typography>
                <JsonInput source="config" label="Config" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};
